import { Table, TableContainer, TableHead, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/material';
import React, { useRef, useMemo } from 'react';
import type { Table as TanStackTable } from '@tanstack/react-table';
import type { ColumnMetadata, LookupDataItem } from '../../../types';
import type { FilterOperators, FilterMenuAnchor } from '../types';
import { DataGridHeader } from './DataGridHeader';
import { DataGridFilterRow } from './DataGridFilterRow';
import { DataGridBody } from './DataGridBody';
import { isImageColumn } from '../utils/columnUtils';
import { DataGridFooter } from './DataGridFooter';
import { GroupedDataGridBody } from './GroupedDataGridBody';
import type { GroupBucket } from '../../../hooks/useGridGrouping';
import { GroupChipsBar } from './GroupChipsBar';
import { getRenderedColumnWidth, getVisibleTableWidth } from './columnSizing';
import {
  getStickyHoverRowBackground,
  getStickySelectedRowBackground,
  getStickyStripedRowBackground,
  getStickyUnreadRowBackground,
} from './rowBackgroundStyles';

type DataGridTableProps<T> = {
  focusRowId?: string;
  table: TanStackTable<T>;
  columnMetadataMap: Map<string, ColumnMetadata>;
  filterOperators: FilterOperators;
  setFilterMenuAnchor: React.Dispatch<React.SetStateAction<FilterMenuAnchor>>;
  lookupMaps?: Map<string, Record<string, string>>;
  filterLookupMaps?: Map<string, Record<string, string>>;
  rawLookupItems?: Map<string, LookupDataItem[]>;
  manualFiltering?: boolean;
  showFilterPanel?: boolean;
  shrinkToRowNumbers?: boolean;
  wordWrap?: boolean;
  headerHeight: number;
  headerRowRef: React.RefObject<HTMLTableRowElement | null>;
  modifiedCells?: Set<string>;
  serverSummary?: string[] | null;
  markedForDeletion?: Set<string>;
  onColumnReorder?: (draggedId: string, targetId: string) => void;
  // ── Grouping ───────────────────────────────────────────────────────────────
  isGrouped?: boolean;
  groupedRows?: GroupBucket<T>[] | null;
  collapsedGroups?: Set<string>;
  onToggleGroup?: (key: string) => void;
  groupFields?: string[];
  groupCaptions?: Map<string, string>;
  onGroupReorder?: (fromIdx: number, toIdx: number) => void;
  onGroupRemove?: (field: string) => void;
  onGroupAdd?: (field: string) => void;
};

const FILTER_ROW_HEIGHT = 36;
const STANDARD_ROW_HEIGHT = 32;
const IMAGE_ROW_HEIGHT = 48;
const MOBILE_ROW_HEIGHT = 40;
const MOBILE_IMAGE_ROW_HEIGHT = 56;
const FOOTER_ROW_HEIGHT = 68;

export const DataGridTable = <T extends Record<string, unknown>>({
  focusRowId,
  table,
  columnMetadataMap,
  filterOperators,
  setFilterMenuAnchor,
  lookupMaps,
  filterLookupMaps,
  rawLookupItems,
  manualFiltering,
  showFilterPanel = true,
  shrinkToRowNumbers = false,
  wordWrap = false,
  headerHeight,
  headerRowRef,
  modifiedCells,
  serverSummary,
  markedForDeletion,
  onColumnReorder,
  isGrouped = false,
  groupedRows,
  collapsedGroups,
  onToggleGroup,
  groupFields,
  groupCaptions,
  onGroupReorder,
  onGroupRemove,
  onGroupAdd,
}: DataGridTableProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(800));

  const hasImageColumn = useMemo(
    () => Array.from(columnMetadataMap.values()).some(isImageColumn),
    [columnMetadataMap]
  );
  const rowHeight = hasImageColumn
    ? isMobile
      ? MOBILE_IMAGE_ROW_HEIGHT
      : IMAGE_ROW_HEIGHT
    : isMobile
      ? MOBILE_ROW_HEIGHT
      : STANDARD_ROW_HEIGHT;
  const hasFooter = useMemo(
    () =>
      Array.from(columnMetadataMap.values()).some(
        (col) => col.totalSummaries != null && col.totalSummaries !== ''
      ),
    [columnMetadataMap]
  );

  const containerHeight = shrinkToRowNumbers
    ? `${
        Math.min(table.getState().pagination.pageSize, table.getRowModel().rows.length) *
          rowHeight +
        headerHeight +
        (showFilterPanel ? FILTER_ROW_HEIGHT : 0) +
        (hasFooter ? FOOTER_ROW_HEIGHT : 0)
      }px`
    : '100%';

  const groupingEnabled = useMemo(
    () => Array.from(columnMetadataMap.values()).some((col) => col.groupIndex != null),
    [columnMetadataMap]
  );
  const hiddenGroupFields = useMemo(() => new Set(groupFields ?? []), [groupFields]);
  const visibleLeafColumns = table
    .getVisibleLeafColumns()
    .filter((col) => !hiddenGroupFields.has(col.id));
  const visibleTableWidth = getVisibleTableWidth(visibleLeafColumns, isSmallMobile);
  const hasRows = typeof table.getRowModel === 'function' && table.getRowModel().rows.length > 0;

  return (
    <Box
      sx={
        shrinkToRowNumbers
          ? { display: 'flex', flexDirection: 'column' }
          : { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
      }
    >
      {!!groupFields &&
        groupingEnabled &&
        !!groupCaptions &&
        !!onGroupReorder &&
        !!onGroupRemove && (
          <GroupChipsBar
            groupFields={groupFields}
            groupCaptions={groupCaptions}
            onReorder={onGroupReorder}
            onRemove={onGroupRemove}
            onAddField={onGroupAdd}
          />
        )}

      <TableContainer
        ref={scrollContainerRef}
        sx={{
          flexGrow: shrinkToRowNumbers ? 0 : 1,
          height: shrinkToRowNumbers ? 'auto !important' : containerHeight,
          minHeight: 0,
          minWidth: 0,
          overflow: shrinkToRowNumbers ? 'visible !important' : 'auto',
          position: 'relative',
          backgroundColor: 'background.paper',
        }}
      >
        <Table
          sx={{
            width: '100%',
            height: shrinkToRowNumbers || hasRows ? 'auto' : '100%',
            minWidth: shrinkToRowNumbers ? '100%' : `${visibleTableWidth}px`,
            tableLayout: 'fixed',
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: { xs: '0.875rem', md: '0.8125rem' },
            '& .MuiTableCell-root': {
              border: 0,
              borderRight: '1px solid',
              borderBottom: '1px solid',
              borderRightColor: 'divider',
              borderBottomColor: 'divider',
              fontSize: { xs: '0.875rem', md: '0.8125rem' },
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.text.secondary
                  : theme.palette.text.primary,
              ...(wordWrap && {
                whiteSpace: 'normal',
                overflowWrap: 'normal',
                wordBreak: 'normal',
              }),
            },
            '& tr > .MuiTableCell-root:first-of-type': {
              borderLeft: '1px solid',
              borderLeftColor: 'divider',
            },
            '& thead tr:first-of-type .MuiTableCell-root': {
              borderTop: '1px solid',
              borderTopColor: 'divider',
            },
            '& tbody .MuiTableCell-root': {
              fontSize: { xs: '0.875rem', md: '0.8125rem' },
              fontFamily: 'inherit',
              fontVariantNumeric: 'normal',
            },
            '& tbody .MuiTableCell-root span, & tbody .MuiTableCell-root p, & tbody .MuiTableCell-root div, & tbody .MuiTableCell-root input, & tbody .MuiTableCell-root textarea, & tbody .MuiTableCell-root select, & tbody .MuiTableCell-root button':
              {
                fontSize: { xs: '0.875rem', md: '0.8125rem' },
                fontFamily: 'inherit',
                fontVariantNumeric: 'normal',
              },
            '& tbody .MuiTableCell-root .MuiSvgIcon-root': {
              fontVariantNumeric: 'normal',
            },
            '& thead .MuiTableCell-root, & tfoot .MuiTableCell-root': {
              fontSize: 'inherit',
            },
            '& .MuiTableCell-root[data-soft-deleted="true"]': {
              color: (theme) => theme.palette.error.main,
            },
            '& .MuiTableRow-root[data-unread="true"] .MuiTableCell-root': {
              fontWeight: 650,
            },
            '& .MuiTableCell-root input, & .MuiTableCell-root textarea, & .MuiTableCell-root select':
              {
                color: 'inherit',
              },
            '& .MuiTableCell-root input::placeholder, & .MuiTableCell-root textarea::placeholder': {
              color: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.text.disabled : 'inherit',
              opacity: 1,
            },
            '& .MuiTableCell-root .MuiSvgIcon-root': {
              color: 'inherit',
            },
            // Sticky first column (_navigate)
            '& td[data-col-id="_navigate"]': {
              position: 'sticky',
              left: 0,
              zIndex: 3,
              backgroundColor: 'background.paper',
              boxShadow: '2px 0 5px -3px rgba(0,0,0,0.18)',
            },
            '& tbody tr:nth-of-type(even) td[data-col-id="_navigate"]': {
              backgroundColor: getStickyStripedRowBackground,
            },
            '& tbody tr:hover td[data-col-id="_navigate"]': {
              backgroundColor: getStickyHoverRowBackground,
            },
            '& tbody tr.Mui-selected td[data-col-id="_navigate"]': {
              backgroundColor: getStickySelectedRowBackground,
            },
            '& tbody tr[data-unread="true"] td[data-col-id="_navigate"]': {
              backgroundColor: (theme) => getStickyUnreadRowBackground(theme),
            },
            '& tbody tr[data-unread="true"]:hover td[data-col-id="_navigate"]': {
              backgroundColor: (theme) => getStickyUnreadRowBackground(theme, true),
            },
            '& th[data-col-id="_navigate"]': {
              position: 'sticky',
              left: 0,
              zIndex: 12,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : theme.palette.background.default,
              boxShadow: '2px 0 5px -3px rgba(0,0,0,0.18)',
            },
            '& .MuiTableCell-root input, & .MuiTableCell-root select': {
              color: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiTableFooter-root .MuiTableCell-root': {
              borderLeft: 'none',
              borderRight: 'none',
            },
            '& .MuiTableFooter-root .MuiTableCell-root:first-of-type': {
              borderLeft: '1px solid',
              borderLeftColor: 'divider',
            },
            '& .MuiTableFooter-root .MuiTableCell-root:last-of-type': {
              borderRight: '1px solid',
              borderRightColor: 'divider',
            },
          }}
        >
          <colgroup>
            {visibleLeafColumns.map((col) => {
              return (
                <col key={col.id} style={{ width: getRenderedColumnWidth(col, isSmallMobile) }} />
              );
            })}
          </colgroup>

          <TableHead
            sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <React.Fragment key={headerGroup.id}>
                <DataGridHeader
                  headerGroup={headerGroup}
                  headerRowRef={headerRowRef}
                  onColumnReorder={onColumnReorder}
                  hiddenColumnIds={hiddenGroupFields}
                />
                {showFilterPanel && (
                  <DataGridFilterRow
                    headerGroup={headerGroup}
                    columnMetadataMap={columnMetadataMap}
                    filterOperators={filterOperators}
                    setFilterMenuAnchor={setFilterMenuAnchor}
                    lookupMaps={filterLookupMaps ?? lookupMaps}
                    rawLookupItems={rawLookupItems}
                    manualFiltering={manualFiltering}
                    headerHeight={headerHeight}
                    hiddenColumnIds={hiddenGroupFields}
                  />
                )}
              </React.Fragment>
            ))}
          </TableHead>

          {isGrouped && groupedRows ? (
            <GroupedDataGridBody
              table={table}
              groupedRows={groupedRows}
              collapsedGroups={collapsedGroups ?? new Set()}
              onToggleGroup={onToggleGroup ?? (() => {})}
              columnMetadataMap={columnMetadataMap}
              groupCaptions={groupCaptions ?? new Map()}
              modifiedCells={modifiedCells}
              markedForDeletion={markedForDeletion}
              rowHeight={rowHeight}
              wordWrap={wordWrap}
              hiddenColumnIds={hiddenGroupFields}
            />
          ) : (
            <DataGridBody
              focusRowId={focusRowId}
              table={table}
              modifiedCells={modifiedCells}
              markedForDeletion={markedForDeletion}
              scrollContainerRef={scrollContainerRef}
              rowHeight={rowHeight}
              wordWrap={wordWrap}
              hiddenColumnIds={hiddenGroupFields}
            />
          )}

          <DataGridFooter
            table={table}
            columnMetadataMap={columnMetadataMap}
            serverSummary={serverSummary}
            hiddenColumnIds={hiddenGroupFields}
          />
        </Table>
      </TableContainer>
    </Box>
  );
};
